// frontend/components/HrEmployeeProfileTab.jsx
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyEmployeeProfile,
  fetchMyVacationSummary,
} from "../features/hr/hrThunks.js";
import { Briefcase, CalendarDays, Clock, Mail, Phone, User } from "lucide-react";
import "../styles/hr/hremployeeprofile.css";

const getEmploymentTypeLabel = (employmentType) => {
  if (employmentType === "full_time") return "Jornada completa";
  if (employmentType === "part_time") return "Media jornada";
  if (employmentType === "contractor") return "Colaborador externo";
  if (employmentType === "intern") return "Prácticas";
  return employmentType || "-";
};

const getStatusLabel = (status) => {
  if (status === "active") return "Activo";
  if (status === "on_leave") return "En baja / permiso";
  if (status === "inactive") return "Inactivo";
  return status || "-";
};

const getStatusClass = (status) => {
  if (status === "active") return "hr-pill--status-active";
  if (status === "on_leave") return "hr-pill--status-leave";
  if (status === "inactive") return "hr-pill--status-inactive";
  return "hr-pill--status-active";
};

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const HrEmployeeProfileTab = () => {
  const dispatch = useDispatch();

  const employeeProfile = useSelector((state) => state.hr.employeeProfile);
  const loadingEmployeeProfile = useSelector(
    (state) => state.hr.loadingEmployeeProfile
  );
  const errorEmployeeProfile = useSelector(
    (state) => state.hr.errorEmployeeProfile
  );

  const myVacationSummary = useSelector((state) => state.hr.myVacationSummary);
  const loadingVacationSummary = useSelector(
    (state) => state.hr.loadingVacationSummary
  );
  const errorVacationSummary = useSelector(
    (state) => state.hr.errorVacationSummary
  );

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    dispatch(fetchMyEmployeeProfile());
    dispatch(fetchMyVacationSummary({ year: currentYear }));
  }, [dispatch, currentYear]);

  if (loadingEmployeeProfile) {
    return <div>Cargando perfil de empleado...</div>;
  }

  const profileErrorText = getErrorText(errorEmployeeProfile);
  if (profileErrorText) {
    return <div className="hr-error-text">Error: {profileErrorText}</div>;
  }

  if (!employeeProfile) {
    return <div>No hay perfil de empleado disponible.</div>;
  }

  const user = employeeProfile.user || {};

  const fullName = (() => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    const combined = `${firstName} ${lastName}`.trim();
    return combined || user.full_name || user.username || "Sin nombre";
  })();

  const email = user.email || "-";
  const phone = user.phone || employeeProfile.phone || "-";

  const employmentTypeLabel = getEmploymentTypeLabel(employeeProfile.employment_type);
  const statusLabel = getStatusLabel(employeeProfile.status);
  const statusClassName = getStatusClass(employeeProfile.status);

  const managerName = (() => {
    const managerUser = employeeProfile.manager?.user || employeeProfile.manager || {};
    const managerFullName =
      managerUser.full_name ||
      `${managerUser.first_name || ""} ${managerUser.last_name || ""}`.trim();
    return managerFullName || managerUser.username || "Sin asignar";
  })();

  const yearlyVacationDays =
    myVacationSummary?.annual_vacation_days ??
    employeeProfile.annual_vacation_days ??
    0;

  const usedVacationDays =
    myVacationSummary?.used_vacation_days ?? null;

  const remainingVacationDays =
    myVacationSummary?.remaining_vacation_days ?? null;

  const vacationErrorText = getErrorText(errorVacationSummary);

  const remainingText = loadingVacationSummary
    ? "..."
    : remainingVacationDays ?? "-";

  const usedText = loadingVacationSummary
    ? "..."
    : usedVacationDays ?? "-";

  return (
    <div className="hr-profile-tab">
      <div className="hr-profile-header-card">
        <div className="hr-profile-header-main">
          <div className="hr-profile-avatar" aria-hidden="true">
            {user.profile_picture_url ? (
              <img src={user.profile_picture_url} alt={fullName} />
            ) : (
              <span>{String(fullName || " ").trim().charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="hr-profile-header-text">
            <h2 className="hr-profile-name" title={fullName}>
              {fullName}
            </h2>
            <p className="hr-profile-position">
              {employeeProfile.position || "Puesto no definido"}
            </p>
            <p className="hr-profile-department">
              {employeeProfile.department || "Sin departamento"}
            </p>
          </div>
        </div>

        <div className="hr-profile-header-tags">
          <span className={`hr-pill hr-pill--status ${statusClassName}`}>
            {statusLabel}
          </span>
          <span className="hr-pill hr-pill--type">{employmentTypeLabel}</span>
        </div>
      </div>

      <div className="hr-profile-grid">
        <div className="hr-profile-metrics">
          <div className="hr-metric-card hr-metric-card--primary">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Vacaciones disponibles</span>
              <CalendarDays className="hr-metric-icon" aria-hidden="true" />
            </div>

            <div className="hr-metric-value">
              {remainingText}
              <span className="hr-metric-unit">días</span>
            </div>

            <p className="hr-metric-helper">Total anual: {yearlyVacationDays} días</p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Vacaciones usadas</span>
            </div>

            <div className="hr-metric-value">
              {usedText}
              <span className="hr-metric-unit">días</span>
            </div>

            <p className="hr-metric-helper">
              Revisa el detalle en la pestaña <strong>Vacaciones</strong>.
            </p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Horas semanales</span>
              <Clock className="hr-metric-icon" aria-hidden="true" />
            </div>

            <div className="hr-metric-value">
              {employeeProfile.weekly_hours ?? 0}
              <span className="hr-metric-unit">h/sem</span>
            </div>

            <p className="hr-metric-helper">Tipo de jornada: {employmentTypeLabel}.</p>
          </div>
        </div>

        <div className="hr-info-card">
          <h3 className="hr-info-title">
            <Briefcase className="hr-info-icon" aria-hidden="true" />
            Información laboral
          </h3>

          <div className="hr-info-grid">
            <div className="hr-info-row">
              <span className="hr-info-label">Fecha de alta</span>
              <span className="hr-info-value">{employeeProfile.start_date || "-"}</span>
            </div>

            <div className="hr-info-row">
              <span className="hr-info-label">Fecha de baja</span>
              <span className="hr-info-value">{employeeProfile.end_date || "—"}</span>
            </div>

            <div className="hr-info-row">
              <span className="hr-info-label">Departamento</span>
              <span className="hr-info-value">
                {employeeProfile.department || "Sin departamento"}
              </span>
            </div>

            <div className="hr-info-row">
              <span className="hr-info-label">Responsable</span>
              <span className="hr-info-value">{managerName}</span>
            </div>
          </div>
        </div>

        <div className="hr-info-card">
          <h3 className="hr-info-title">
            <User className="hr-info-icon" aria-hidden="true" />
            Datos de contacto
          </h3>

          <div className="hr-info-grid">
            <div className="hr-info-row">
              <span className="hr-info-label">
                <Mail className="hr-inline-icon" aria-hidden="true" />
                Email
              </span>
              <span className="hr-info-value">{email}</span>
            </div>

            <div className="hr-info-row">
              <span className="hr-info-label">
                <Phone className="hr-inline-icon" aria-hidden="true" />
                Teléfono
              </span>
              <span className="hr-info-value">{phone}</span>
            </div>

            <div className="hr-info-row">
              <span className="hr-info-label">Usuario</span>
              <span className="hr-info-value">{user.username || "—"}</span>
            </div>
          </div>
        </div>
      </div>

      {vacationErrorText ? (
        <p className="hr-error-text small">
          No se pudo cargar el resumen de vacaciones: {vacationErrorText}
        </p>
      ) : null}
    </div>
  );
};

export default HrEmployeeProfileTab;